import {
  AppTheme,
  UserChatSettingDisplayFormat,
  UserChatSettingDisplayRange,
} from 'enums/app'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useInsertOrReplaceSettingMutation } from '@crew/apis/setting/settingApis'
import { ValidateRules } from '@crew/utils/form'
import { InsertOrReplaceSettingRequest } from '@crew/apis/setting/models/insertOrReplaceSetting/request'
import { EventTimePeriod, SettingKeyType, Sort } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/i18n'
import { FavoriteDisplayOrderType } from '@crew/enums/domain'

export const DEFAULT_LIST_DISPLAY_NUMBER = 100

export type FormValues = {
  theme: string
  displayNumber: number
  displayFormat: string
  displayRange: string
  historyDisplayOrder: string
  showNewArrival: boolean
  showMySchedule: boolean
  showMyTask: boolean
  showStartUpGuide: boolean
  displayTutorial: boolean
  scheduleInitialDisplay: string
  favoriteDisplayOrderType: string
}
export const formInitialValues: FormValues = {
  theme: AppTheme.Light, //default theme
  displayNumber: DEFAULT_LIST_DISPLAY_NUMBER, //default display number
  displayFormat: UserChatSettingDisplayFormat.Timeline.value, //default display format
  displayRange: UserChatSettingDisplayRange.DisplayAll.value, //default display range (all)
  historyDisplayOrder: Sort.Ascending.value, //default display order (asc)
  showNewArrival: false, // 表示しない
  showMySchedule: true, // 表示する
  showMyTask: true, // 表示する
  showStartUpGuide: true, // 表示する
  displayTutorial: true, // 表示する
  scheduleInitialDisplay: EventTimePeriod.GroupMonthly.value, // グループ月間
  favoriteDisplayOrderType: FavoriteDisplayOrderType.Activity, // アクティビティ順
}

export const usePersonalSettingDisplayForm = () => {
  const { t } = useTranslation()

  const { control, reset, handleSubmit, setError } = useForm<FormValues>({
    criteriaMode: 'all',
    defaultValues: formInitialValues,
  })

  // Get update user setting function
  const [
    insertOrReplaceSettingMutation,
    { isLoading: isLoadingInsertOrReplaceSetting },
  ] = useInsertOrReplaceSettingMutation()

  // Update user setting process
  const updateUserSettings = useCallback(
    async (data: FormValues) => {
      // user setting params
      const userSettingData: InsertOrReplaceSettingRequest = {
        userSettings: [
          {
            key: SettingKeyType.AppearanceTheme,
            value: data.theme,
          },
          {
            key: SettingKeyType.ListDisplayNumber,
            value: String(data.displayNumber),
          },
          {
            key: SettingKeyType.ChatDisplayFormat,
            value: data.displayFormat,
          },
          {
            key: SettingKeyType.ChatDisplayRange,
            value: data.displayRange,
          },
          {
            key: SettingKeyType.TaskHistoryDisplayOrder,
            value: data.historyDisplayOrder,
          },
          {
            key: SettingKeyType.ShowNewArrival,
            value: String(data.showNewArrival),
          },
          {
            key: SettingKeyType.ShowMySchedule,
            value: String(data.showMySchedule),
          },
          {
            key: SettingKeyType.ShowMyTask,
            value: String(data.showMyTask),
          },
          {
            key: SettingKeyType.ShowStartUpGuide,
            value: String(data.showStartUpGuide),
          },
          {
            key: SettingKeyType.DisplayTutorial,
            value: String(data.displayTutorial),
          },
          {
            key: SettingKeyType.ScheduleInitialDisplay,
            value: String(data.scheduleInitialDisplay),
          },
          {
            key: SettingKeyType.FavoriteDisplayOrderType,
            value: String(data.favoriteDisplayOrderType),
          },
        ],
      }
      // call api save setting user
      await insertOrReplaceSettingMutation(userSettingData).unwrap()
    },
    [insertOrReplaceSettingMutation]
  )

  // バリデーションルール
  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      displayFormat: {
        required: t('message.general.required'),
      },
      displayNumber: {
        required: t('message.general.required'),
      },
      displayRange: {
        required: t('message.general.required'),
      },
      historyDisplayOrder: {
        required: t('message.general.required'),
      },
      theme: {
        required: t('message.general.required'),
      },
      showNewArrival: {},
      showMySchedule: {},
      showMyTask: {},
      showStartUpGuide: {},
      displayTutorial: {},
      scheduleInitialDisplay: {
        required: t('message.general.required'),
      },
      favoriteDisplayOrderType: {
        required: t('message.general.required'),
      },
    }),
    [t]
  )

  return {
    control,
    reset,
    handleSubmit,
    setError,
    validateRules,
    updateUserSettings,
    isLoadingInsertOrReplaceSetting,
  }
}
